import { useMemo, useState } from "react";
import { Flag, Images, ImageOff, DoorOpen, Check } from "lucide-react";
import { createToast } from "vercel-toast";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { visionService } from "@/services/vision-service";
import type { RankedSpace } from "../types";

export interface ReportReason {
  value: string;
  label: string;
  requiresDescription?: boolean;
}

export interface ReportType {
  value: string;
  label: string;
  messageLabel: string;
  icon: typeof Flag;
  reasons: ReportReason[];
  photoSpecific?: boolean;
}

export const REPORT_TYPES: ReportType[] = [
  {
    value: "request_new_photos",
    label: "Solicitar novas fotos",
    messageLabel: "Solicitar novas fotos",
    icon: Images,
    reasons: [
      { value: "space_no_photos", label: "Espaço sem fotos" },
      {
        value: "insufficient_photos",
        label: "Poucas fotos ou ângulos insuficientes",
      },
      {
        value: "outdated_photos",
        label: "Fotos desatualizadas do local (pedir fotos novas)",
      },
      { value: "other", label: "Outro", requiresDescription: true },
    ],
  },
  {
    value: "photo_issue",
    label: "Reportar problema com a foto",
    messageLabel: "Problema com a foto",
    icon: ImageOff,
    photoSpecific: true,
    reasons: [
      { value: "photo_mismatch", label: "Foto não corresponde ao local" },
      {
        value: "low_quality",
        label: "Baixa qualidade (escura, distorcida, resolução ruim)",
      },
      {
        value: "identifies_location",
        label: "Foto identifica o local (exibe logo ou marca do espaço)",
      },
      { value: "other", label: "Outro", requiresDescription: true },
    ],
  },
  {
    value: "room_issue",
    label: "Reportar problema com a sala",
    messageLabel: "Problema com a sala",
    icon: DoorOpen,
    reasons: [
      {
        value: "not_private_office",
        label:
          "Este local não é um Escritório Privativo (tipo de espaço errado)",
      },
      {
        value: "incorrect_info",
        label:
          "Informações da sala incorretas (capacidade, nome, localização, valores, etc)",
        requiresDescription: true,
      },
      { value: "other", label: "Outro", requiresDescription: true },
    ],
  },
];

const RAQUEL_SLACK_USER_ID = "U07DFTEDUEN";
const THAIANE_SLACK_USER_ID = "U091A5F4GU8";

export function resolveRoomId(space: RankedSpace): string {
  return space.searchId != null && space.searchId !== 0
    ? String(space.searchId)
    : space.id;
}

export function buildRoomLabel(space: RankedSpace): string {
  return [space.roomName, space.spaceName, space.city]
    .filter(Boolean)
    .join(" · ");
}

export function buildSpacesUrl(space: RankedSpace): string | null {
  const portalUrl = import.meta.env.VITE_PORTAL_URL;
  if (!portalUrl) return null;
  return `${portalUrl}/workspaces/private-office/details/${resolveRoomId(space)}`;
}

export function spacePhotoUrls(space: RankedSpace): string[] {
  return Array.from(
    new Set([...(space.images ?? []), ...(space.spacePhotos ?? [])]),
  );
}

export function buildSlackMessage(
  type: ReportType,
  reason: ReportReason,
  description: string,
  space: RankedSpace,
  reporterEmail?: string | null,
  photoUrls?: string[] | null,
): string {
  const lines = [
    `📸 Novo reporte de fotos | Vision`,
    ``,
    `Tipo: ${type.messageLabel}`,
    `Motivo: ${reason.label}`,
  ];
  if (reason.requiresDescription) {
    lines.push(`Descrição: "${description.trim()}"`);
  }

  const urls = photoUrls ?? [];
  if (urls.length === 1) {
    lines.push(`Foto: ${urls[0]}`);
  } else if (urls.length > 1) {
    lines.push(`Fotos:`);
    for (const url of urls) lines.push(`- ${url}`);
  }
  lines.push(`Sala: ${buildRoomLabel(space)}`);
  lines.push(`ID da sala: ${resolveRoomId(space)}`);
  const spacesUrl = buildSpacesUrl(space);
  if (spacesUrl) lines.push(`Link: ${spacesUrl}`);
  lines.push(`Reportado por: ${reporterEmail ?? "—"}`);
  lines.push(``);
  lines.push(`cc <@${RAQUEL_SLACK_USER_ID}> <@${THAIANE_SLACK_USER_ID}>`);
  return lines.join("\n");
}

interface ReportProblemsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  spaces: RankedSpace[];
  reporterEmail?: string | null;
}

export function ReportProblemsModal({
  open,
  onOpenChange,
  spaces,
  reporterEmail,
}: ReportProblemsModalProps) {
  const [spaceId, setSpaceId] = useState<string>(() => spaces[0]?.id ?? "");
  const [typeValue, setTypeValue] = useState<string | null>(null);
  const [reasonValue, setReasonValue] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const selectedType = useMemo(
    () => REPORT_TYPES.find((t) => t.value === typeValue) ?? null,
    [typeValue],
  );
  const selectedReason = useMemo(
    () => selectedType?.reasons.find((r) => r.value === reasonValue) ?? null,
    [selectedType, reasonValue],
  );
  const selectedSpace = useMemo(
    () => spaces.find((s) => s.id === spaceId) ?? spaces[0] ?? null,
    [spaces, spaceId],
  );
  const photos = useMemo(
    () => (selectedSpace ? spacePhotoUrls(selectedSpace) : []),
    [selectedSpace],
  );

  const needsDescription = selectedReason?.requiresDescription ?? false;
  const needsPhoto =
    (selectedType?.photoSpecific ?? false) && photos.length > 0;
  const canSubmit =
    selectedSpace != null &&
    selectedType != null &&
    selectedReason != null &&
    (!needsDescription || description.trim().length > 0) &&
    (!needsPhoto || photoUrls.length > 0);

  const togglePhoto = (url: string) => {
    setPhotoUrls((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url],
    );
  };

  const reset = () => {
    setSpaceId(spaces[0]?.id ?? "");
    setTypeValue(null);
    setReasonValue(null);
    setDescription("");
    setPhotoUrls([]);
    setSubmitting(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSelectType = (value: string) => {
    setTypeValue(value);
    setReasonValue(null);
    setDescription("");
    setPhotoUrls([]);
  };

  const handleSubmit = () => {
    if (!canSubmit || !selectedType || !selectedReason || !selectedSpace)
      return;

    const orderedPhotos = photos.filter((u) => photoUrls.includes(u));

    setSubmitting(true);
    visionService.notifySlack(
      "photo_issue_reported",
      buildSlackMessage(
        selectedType,
        selectedReason,
        description,
        selectedSpace,
        reporterEmail,
        needsPhoto ? orderedPhotos : null,
      ),
    );
    createToast("Reporte enviado com sucesso. Obrigado!", {
      timeout: 4000,
      type: "success",
    });
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden gap-0">
        <DialogHeader
          className="px-5 pt-5 pb-4"
          style={{ background: "#1A1814" }}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
              style={{ background: "rgba(255,217,0,0.15)" }}
            >
              <Flag className="w-4 h-4" style={{ color: "#FFD900" }} />
            </div>
            <DialogTitle
              className="text-sm font-semibold leading-snug"
              style={{ color: "white" }}
            >
              Relatar problemas
            </DialogTitle>
          </div>
          <DialogDescription
            className="text-xs mt-1"
            style={{ color: "rgba(255,255,255,0.6)" }}
          >
            Escolha a sala, o tipo e o motivo do reporte.
          </DialogDescription>
        </DialogHeader>

        <div
          className="px-5 py-4 flex flex-col gap-4 max-h-[70vh] overflow-y-auto"
          style={{ background: "#FDFBF7" }}
        >
          {spaces.length > 1 && (
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="report-space"
                className="text-xs font-semibold"
                style={{ color: "#43403D" }}
              >
                Sala
              </label>
              <select
                id="report-space"
                value={spaceId}
                onChange={(e) => {
                  setSpaceId(e.target.value);
                  setPhotoUrls([]);
                }}
                className="h-10 rounded-xl text-sm px-3 bg-white"
                style={{ border: "1px solid #E8E6E3", color: "#43403D" }}
              >
                {spaces.map((s) => (
                  <option key={s.id} value={s.id}>
                    {buildRoomLabel(s)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <span
              className="text-xs font-semibold"
              style={{ color: "#43403D" }}
            >
              Tipo
            </span>
            <div
              role="radiogroup"
              aria-label="Tipo de reporte"
              className="flex flex-col gap-2"
            >
              {REPORT_TYPES.map((type) => {
                const isActive = typeValue === type.value;
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    onClick={() => handleSelectType(type.value)}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-left transition-all duration-150"
                    style={{
                      background: isActive ? "#1A1814" : "#FFFFFF",
                      color: isActive ? "#FFD900" : "#43403D",
                      border: `1px solid ${isActive ? "#1A1814" : "#E8E6E3"}`,
                    }}
                  >
                    <Icon
                      className="w-4 h-4 shrink-0"
                      style={{ color: isActive ? "#FFD900" : "#8C7200" }}
                    />
                    <span className="flex-1">{type.label}</span>
                    {isActive && <Check className="w-4 h-4 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>

          {selectedType && (
            <div className="flex flex-col gap-2">
              <span
                className="text-xs font-semibold"
                style={{ color: "#43403D" }}
              >
                Motivo
              </span>
              <div
                role="radiogroup"
                aria-label="Motivo do reporte"
                className="flex flex-col gap-2"
              >
                {selectedType.reasons.map((reason) => {
                  const isActive = reasonValue === reason.value;
                  return (
                    <button
                      key={reason.value}
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      onClick={() => setReasonValue(reason.value)}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-all duration-150"
                      style={{
                        background: isActive ? "#FFF7CC" : "#FFFFFF",
                        color: "#43403D",
                        border: `1px solid ${isActive ? "#FFD900" : "#E8E6E3"}`,
                      }}
                    >
                      <span className="flex-1">{reason.label}</span>
                      {isActive && (
                        <Check
                          className="w-4 h-4 shrink-0"
                          style={{ color: "#8C7200" }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {selectedType?.photoSpecific && photos.length > 0 && (
            <div className="flex flex-col gap-2">
              <span
                className="text-xs font-semibold"
                style={{ color: "#43403D" }}
              >
                Fotos{" "}
                <span style={{ color: "#8C7200", fontWeight: 400 }}>
                  (selecione uma ou mais)
                </span>
              </span>
              <div
                role="group"
                aria-label="Fotos do reporte"
                className="grid grid-cols-4 gap-2"
              >
                {photos.map((url) => {
                  const isActive = photoUrls.includes(url);
                  return (
                    <button
                      key={url}
                      type="button"
                      role="checkbox"
                      aria-checked={isActive}
                      aria-label={`Selecionar foto ${url}`}
                      onClick={() => togglePhoto(url)}
                      className="relative aspect-square overflow-hidden rounded-lg transition-all duration-150"
                      style={{
                        outline: isActive
                          ? "3px solid #FFD900"
                          : "1px solid #E8E6E3",
                        outlineOffset: isActive ? "2px" : "0",
                        boxShadow: isActive
                          ? "0 0 0 1px #1A1814, 0 4px 12px rgba(255,217,0,0.45)"
                          : "none",
                        transform: isActive ? "scale(0.95)" : "scale(1)",
                      }}
                    >
                      <img
                        src={url}
                        alt=""
                        className="w-full h-full object-cover"
                        style={{
                          filter: isActive ? "brightness(0.7)" : "none",
                        }}
                      />
                      {isActive && (
                        <span className="absolute inset-0 flex items-center justify-center">
                          <span
                            className="flex items-center justify-center rounded-full shadow-md"
                            style={{
                              width: 22,
                              height: 22,
                              background: "#FFD900",
                            }}
                          >
                            <Check
                              className="w-3.5 h-3.5"
                              strokeWidth={3}
                              style={{ color: "#1A1814" }}
                            />
                          </span>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {needsDescription && (
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="report-problem-description"
                className="text-xs font-semibold"
                style={{ color: "#43403D" }}
              >
                Descrição
              </label>
              <Textarea
                id="report-problem-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descreva o problema..."
                className="min-h-[90px] resize-none rounded-xl text-sm"
                style={{ borderColor: "#E8E6E3" }}
              />
            </div>
          )}
        </div>

        <DialogFooter
          className="px-5 py-4 gap-2 sm:gap-2"
          style={{ background: "#FDFBF7", borderTop: "1px solid #ECEAE6" }}
        >
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            style={{ borderColor: "#E8E6E3", color: "#43403D" }}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="gap-1.5"
            style={{
              background: "#FFD900",
              color: "#1A1814",
              borderColor: "#FFD900",
            }}
          >
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
